import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, MessageSquare, ShieldCheck, Radar, PhoneCall, Send,
  History, BarChart3, RefreshCw, Lock, Layers,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

// One registry powers the module sub-nav, the pillar grid and cross-links.
export const MODULES = [
  { slug: "prospecting", name: "Prospecting & Signals", agent: "Scout", icon: Radar, blurb: "Finds the right prospects and watches for buying signals — you approve before anything activates." },
  { slug: "receptionist", name: "AI Receptionist", agent: "Ava", icon: PhoneCall, blurb: "Answers chat and calls in many languages, qualifies, books and hands off to a human." },
  { slug: "outreach", name: "Outreach & Sequences", agent: "Relay", icon: Send, blurb: "A visual, consent-first sequence builder across email, SMS, WhatsApp, voice and chat." },
  { slug: "timeline", name: "Customer Timeline & CRM", agent: null, icon: History, blurb: "One chronological record per customer, synced two-way with your CRM." },
  { slug: "dashboard", name: "Revenue Intelligence", agent: null, icon: BarChart3, blurb: "From page views to booked meetings to revenue — attributed by source, campaign and agent." },
  { slug: "revival", name: "Revival & Growth", agent: null, icon: RefreshCw, blurb: "Packaged automations that revive cold leads and grow existing customers respectfully." },
  { slug: "trust", name: "Trust & Compliance", agent: null, icon: Lock, blurb: "Consent, suppression, deliverability and audit logs — built in, not bolted on." },
  { slug: "packages", name: "Packages", agent: null, icon: Layers, blurb: "Three levels — from a small-business starter to a full AI revenue workforce." },
];

export const AGENTS = [
  { name: "Scout", role: "Prospecting & Signals", icon: Radar, to: "/ai-revenue-center/prospecting", color: "#2563EB", tag: "Finds & watches", line: "Builds your ideal-customer profile, enriches records and flags real buying signals — always with your approval first." },
  { name: "Ava", role: "Receptionist & Qualification", icon: PhoneCall, to: "/ai-revenue-center/receptionist", color: "#0EA5E9", tag: "Answers & qualifies", line: "Handles website chat and phone calls in multiple languages, asks your qualifying questions and books the meeting." },
  { name: "Relay", role: "Outreach & Nurture", icon: Send, to: "/ai-revenue-center/outreach", color: "#7C3AED", tag: "Follows up", line: "Runs consent-first sequences across email, SMS, WhatsApp and voice — and stops the moment someone opts out." },
];

export function RevShell({ testid, children }) {
  return (
    <>
      <Navbar />
      <main data-testid={testid} className="bg-slate-50">{children}</main>
      <Footer />
    </>
  );
}

// Sub-navigation pill row shared across every module page.
export function ModuleBar() {
  const { pathname } = useLocation();
  return (
    <div className="sticky top-[76px] z-30 bg-white/85 backdrop-blur border-b border-slate-100" data-testid="rev-module-bar">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar py-2.5">
          <Link to="/ai-revenue-center" data-testid="rev-nav-overview"
            className={`shrink-0 text-[13px] font-semibold rounded-full px-3.5 py-1.5 transition-colors ${pathname === "/ai-revenue-center" ? "bg-brand text-white" : "text-slate-500 hover:text-brand hover:bg-blue-50"}`}>Overview</Link>
          {MODULES.map((m) => {
            const to = `/ai-revenue-center/${m.slug}`;
            const active = pathname === to;
            return (
              <Link key={m.slug} to={to} data-testid={`rev-nav-${m.slug}`}
                className={`shrink-0 text-[13px] font-semibold rounded-full px-3.5 py-1.5 transition-colors ${active ? "bg-brand text-white" : "text-slate-500 hover:text-brand hover:bg-blue-50"}`}>{m.name}</Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function RevHero({ eyebrow, title, accent, sub, agent, visual }) {
  return (
    <section className="pt-28 pb-14 bg-white border-b border-slate-100 relative overflow-hidden">
      <div className="absolute -top-32 -right-24 h-96 w-96 rounded-full blur-3xl bg-blue-100/60" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          {agent && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-brand mb-3">
              <span className="h-1.5 w-1.5 rounded-full bg-brand" /> Meet {agent}
            </span>
          )}
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">{eyebrow}</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mt-3">
            {title} {accent && <span className="text-brand">{accent}</span>}
          </h1>
          <p className="text-slate-500 mt-4 text-lg">{sub}</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Link to="/audit" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3.5 bg-brand text-white hover:-translate-y-0.5 transition-transform" data-testid="rev-hero-audit">Start with a free audit <ArrowRight size={16} /></Link>
            <Link to="/ai-revenue-center" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3.5 btn-outline">Explore the Revenue Center</Link>
          </div>
        </motion.div>
        {visual && (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}>
            {visual}
          </motion.div>
        )}
      </div>
    </section>
  );
}

export function SectionHead({ eyebrow, title, sub, center }) {
  return (
    <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
      {eyebrow && <span className="text-xs font-bold uppercase tracking-[0.18em] text-brand">{eyebrow}</span>}
      <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-3">{title}</h2>
      {sub && <p className="text-slate-500 mt-2 text-base">{sub}</p>}
    </div>
  );
}

// A faux browser window that makes an in-page mockup read like a product screenshot.
export function BrowserFrame({ url = "app.websitetech.pro", children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white soft-shadow overflow-hidden ${className}`}>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50">
        <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
        <div className="ml-3 flex-1 text-[11px] text-slate-400 bg-white border border-slate-200 rounded-md px-2.5 py-1 truncate">{url}</div>
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </div>
  );
}

export function StatChip({ label, value, tone = "brand" }) {
  const tones = { brand: "text-brand", emerald: "text-emerald-600", amber: "text-amber-600", slate: "text-slate-900" };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 card-shadow">
      <div className={`text-2xl font-bold ${tones[tone]}`}>{value}</div>
      <div className="text-[11px] text-slate-500 uppercase tracking-wide mt-1">{label}</div>
    </div>
  );
}

export function ComplianceNote({ children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 flex items-start gap-3" data-testid="rev-compliance-note">
      <ShieldCheck size={18} className="text-brand mt-0.5 shrink-0" />
      <p className="text-sm text-slate-600 leading-relaxed">{children}</p>
    </div>
  );
}

export function RevCTA({ title, sub }) {
  return (
    <section className="py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl p-10 md:p-14 text-center bg-brand text-white">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">{title}</h2>
          <p className="mt-3 text-white/80 max-w-xl mx-auto">{sub}</p>
          <div className="flex flex-wrap justify-center gap-3 mt-7">
            <Link to="/audit" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3.5 bg-white text-brand hover:-translate-y-0.5 transition-transform" data-testid="rev-cta-audit">Get my free audit <ArrowRight size={16} /></Link>
            <Link to="/ai-chatbot" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3.5 border border-white/40 hover:bg-white/10 transition-colors"><MessageSquare size={16} /> Talk to WebsiteTech AI</Link>
          </div>
          <p className="text-xs text-white/60 mt-5 max-w-lg mx-auto">Human-supervised AI. We never promise guaranteed rankings, traffic, leads or revenue — and every channel runs on consent.</p>
        </div>
      </div>
    </section>
  );
}
